import React, { useMemo } from "react"

import { OMReference, OMUniverse, User } from "@apptivity-lab/firmament-node-sdk"
import { SUPPORTED_TIME_ZONES, similarTimeZones, useFieldsetModel, capitalize } from "@lib"

import { AffAttendeeProfile } from "@firmament-packages/aff"

import TextInput from "../../forms/TextInput"
import DropdownSelect from "@components/DropdownSelect"
import ProfilePhotoPicker from "@firmament-packages/connectx/components/ProfilePhotoPicker"
import DatePicker from "@components/forms/DatePicker"
import { GenImage } from "@firmament-packages"

type Props = {
    attendeeProfile?: AffAttendeeProfile,
    showDateOfBirth?: boolean
    errors?: { [key: string]: Error }
    disabled?: boolean,
    onChange?: (model?: AffAttendeeProfile) => void
}

const AffAttendeeProfileFieldset: React.FunctionComponent<Props> = ({
    attendeeProfile,
    showDateOfBirth,
    errors,
    disabled,
    onChange
}) => {
    const newProfile = useMemo(() => {
        return new AffAttendeeProfile({
            user: new OMReference(
                User,
                OMUniverse.shared.currentUser && OMUniverse.shared.currentUser.user ? OMUniverse.shared.currentUser?.user?.id : ""
            ),
            timezone: new Intl.DateTimeFormat("en").resolvedOptions().timeZone,
            photo: null,
            dateOfBirth: null,
            participationType: "INDIVIDUAL"
        })
    }, [])

    const [model, setModel] = useFieldsetModel(newProfile, attendeeProfile, onChange)

    return (
        <fieldset disabled={disabled}>
            {model && model.isInserted &&
                <div style={{ textAlign: "center" }} className="mb-[32px]">
                    <ProfilePhotoPicker className="mt-[32px]"
                        value={model?.photo?.actualObject}
                        onChange={(file) => {
                            if (file) {
                                OMUniverse.shared.touch(file)
                            }
                            setModel(Object.assign({}, model, { photo: file ? new OMReference(GenImage, file) : null }))
                        }} />
                </div>
            }

            <div>
                <div className="flex gap-x-[20px]">
                    <TextInput className="flex-1" type="text" title="First Name"
                        isRequired={true}
                        error={(errors || {})["name"]}
                        value={model?.name || undefined}
                        onChange={(value) => setModel(Object.assign({}, model, { name: value ? capitalize(value) : value }))} />
                    <TextInput className="flex-1" type="text" title="Last Name (Optional)"
                        value={model?.lastName || undefined}
                        onChange={(value) => setModel(Object.assign({}, model, { lastName: value ? capitalize(value) : value }))}
                        />
                </div>
            </div>

            {showDateOfBirth &&
                <DatePicker className="mt-[24px]" title="Birthday (Optional)"
                    value={model?.dateOfBirth || undefined}
                    onChange={(value) => setModel(Object.assign({}, model, { dateOfBirth: value }))}
                    />
            }

            {model && model.isInserted &&
                <TextInput type="text" title="Phone Number" isRequired={false}
                    className="mt-[32px]"
                    value={model?.phoneNumber || undefined}
                    onChange={(value) => setModel(Object.assign({}, model, { phoneNumber: value }))}
                    helperText={"Please enter your phone number so we can provide better support for you."}
                    />
            }

            {model && model.isInserted &&
                <>
                    <DropdownSelect
                        className="mt-[32px]"
                        title="Your class times will display in"
                        value={model?.timezone || new Intl.DateTimeFormat("en").resolvedOptions().timeZone }
                        options={ SUPPORTED_TIME_ZONES.map( tz => { return {"title": tz.title, "value": tz.timezone} })}
                        placeholder="Select timezone..."
                        onChange={(value) => setModel(Object.assign({}, model, { timezone: value }))}
                        />
                </>
            }
        </fieldset>
    )
}

export default AffAttendeeProfileFieldset
